// Catalog.ID Client — Organization key entitlements (§3a.4)
// ============================================================================
// THE client-side mirror of the server's `KnownKeyTypes.GetEntitledOrgKeys`
// (apps/cid/src/server/src/CatalogId.Server/KnownKeyTypes.cs): which org keys a
// delegate receives, wrapped to their own `attr_encrypt` key, by role.
//
// A stale mirror used to be destructive rather than merely incomplete: an org
// key rotation (org-workspace-actions.ts) re-wraps only the keys the mirror
// entitles, and the server replaced the stored set with what it was sent — so a
// key missing here lost every delegate's wrapped copy at the next rotation
// (docs/cid-governance.md §1.10). That happened once: two hand-kept copies of
// this table omitted `efs_encrypt`. Since M15 the server keeps every wrap a
// rotation does not resubmit while the delegate's roles still entitle it, so a
// stale mirror now only leaves a delegate without the new generation of a key.
//
// It is therefore pinned to the server by a PARITY TEST, not by a hand-written
// list: org-key-entitlements.test.ts reads KnownKeyTypes.cs itself — its
// constants, `ValidKeyIds` and `GetEntitledOrgKeys` — and fails when the two
// disagree for any combination of roles, and when the server accepts a key type
// this file neither entitles nor lists in NEVER_DELEGATED_KEY_TYPES. Change the
// server first; the test then says what to change here.
//
// The table, as of this writing (the test is the authority):
//   every delegate → attr_encrypt, efs_encrypt, chat_encrypt, chat_sign,
//                    mail_encrypt, mail_sign
//   owner          → + msg_encrypt, xrpl_master, bitcash_wallet, main_signing_key
//   admin          → + msg_encrypt
//   treasurer      → + xrpl_master, bitcash_wallet
//   viewer         → nothing more
//
// Only owners hold `main_signing_key`, so only owners (and the organization
// account itself) sign in the ceremony console.
//
// Deliberately import-free: `apps/cid/dev/seed/organization.ts` pulls this in
// from headless Node, where the state stores and the DOM do not exist. Keep it
// that way.

/**
 * Key types the server accepts in a key bag (`KnownKeyTypes.ValidKeyIds`) that
 * are never wrapped to a delegate, whatever the role: account-held secrets, not
 * organization keys a member works with.
 */
export const NEVER_DELEGATED_KEY_TYPES: readonly string[] = Object.freeze([
    'derive_root',   // the account's HKDF derive root (bag-only)
    'recovery_kit',  // the Recovery Kit's secret-sharing material (bag-only)
]);

/**
 * Org key IDs a delegate holding `roles` is entitled to receive, wrapped to
 * their own `attr_encrypt` key.
 *
 * Every delegate gets `attr_encrypt` (org-wide claim sharing, §3a.3.1),
 * `efs_encrypt` (org-wide EFS sharing — an org-sealed package must open for
 * every delegate), `chat_encrypt` + `chat_sign` (a delegate is chat-reachable in
 * their org capacity; apps/chat/docs/plans/migration.md §3) and `mail_encrypt` +
 * `mail_sign` (inbound mail to an org/role address is encrypted to the org's
 * mail_encrypt key, and members reply under org-issued sub-key certs;
 * apps/mail/docs/plans/migration.md §5.5).
 */
export function getEntitledOrgKeys(roles: readonly string[]): Set<string> {
    const entitled = new Set<string>([
        'attr_encrypt',
        'efs_encrypt',
        'chat_encrypt',
        'chat_sign',
        'mail_encrypt',
        'mail_sign',
    ]);

    for (const role of roles) {
        switch (role) {
            case 'owner':
                entitled.add('msg_encrypt');
                entitled.add('xrpl_master');
                // The organization's bitcash wallet (registration-wallets D14):
                // it pays for the organization's work, so it is entitled to the
                // two roles that may commit its money and to no others. An
                // ADMIN manages people, not money.
                entitled.add('bitcash_wallet');
                // The organization's signing key: bindings, countersignatures and
                // consents (docs/cid-governance.md M6) are owner-signed.
                entitled.add('main_signing_key');
                break;
            case 'admin':
                entitled.add('msg_encrypt');
                break;
            case 'treasurer':
                entitled.add('xrpl_master');
                entitled.add('bitcash_wallet');
                break;
            // 'viewer' (and any unknown role) adds nothing beyond the base set.
        }
    }

    return entitled;
}
